import type { SiteSeparationAttributeTransformation } from './schema/siteSeparation/siteSeparationAttributeTransformation';

export const valueTransformation: Record<
  SiteSeparationAttributeTransformation,
  (v: string) => string
> = {
  lowercase: (str) => (str ? str.toLowerCase() : ''),
  none: (str) => str,
  uppercase: (str) => (str ? str.toUpperCase() : ''),
};

// analyze regular expression string and return number of capturing groups and names of named capturing groups
function getRxCapturingGroups(rxStr: string): {
  numberOfGroups: number;
  groupNames: Set<string>;
} {
  const result = new RegExp(`${rxStr}|`).exec('');
  return result && result.length > 1
    ? {
        numberOfGroups: result.length - 1,
        groupNames: new Set(Object.keys(result.groups || {})),
      }
    : {
        numberOfGroups: 0,
        groupNames: new Set(),
      };
}

type TemplateReference = {
  str: string;
  isNumber: boolean;
};

// analyze template string and return all reference names `${...}` with information if it is position or named reference
const numericRx = /^0$|^[1-9][0-9]*$/;
const referenceRx = /\$\{([^}]+)}/g; // the reference format is '${...}', we are selecting all reference names

export function getTemplateReferences(
  templateStr: string | null,
): TemplateReference[] {
  return templateStr === null
    ? [{ str: '1', isNumber: true }] // backward compatibility with original site separation logic
    : [...templateStr.matchAll(referenceRx)].map((ref) => {
        if (!(1 in ref)) {
          throw new Error('Invalid template string');
        }

        return {
          str: ref[1],
          isNumber: numericRx.test(ref[1]),
        };
      });
}

// check if references in template string are accessible in regular expression exec() results
// returns error message, or `null` on success
export function checkCompatibility(
  rxStr: string,
  templateStr: string | null,
): string | null {
  try {
    new RegExp(rxStr);
  } catch (_e) {
    return `Invalid regular expression "${rxStr}".`;
  }
  const groups = getRxCapturingGroups(rxStr);
  const refs = getTemplateReferences(templateStr);

  // position references, e.g. ${3}
  const numberRefs = refs
    .filter((ref) => ref.isNumber)
    .map((ref) => Number.parseInt(ref.str, 10));
  if (numberRefs.length) {
    const min = Math.min(...numberRefs);
    const max = Math.max(...numberRefs);
    if (min < 0) {
      return `Invalid position reference in template string: $\{${min}\}.`;
    }
    if (max > groups.numberOfGroups) {
      return `Invalid position reference in template string: $\{${max}\}, only ${groups.numberOfGroups} capturing groups detected in regular expression.`;
    }
  }

  // named references, e.g. ${location}
  const namedRefs = refs.filter((ref) => !ref.isNumber).map((ref) => ref.str);
  if (namedRefs.length) {
    for (const namedRef of namedRefs) {
      if (!groups.groupNames.has(namedRef)) {
        return `Named template string reference "${namedRef}" not found in the regular expression.`;
      }
    }
  }

  return null;
}

export function applyRxSiteSeparationRule(
  fieldValue: string | null,
  transformation: SiteSeparationAttributeTransformation,
  rx: RegExp,
  templateStr: string | null,
  references: TemplateReference[],
): string | null {
  const valueTransformed =
    fieldValue && valueTransformation[transformation](fieldValue);
  const execRes = valueTransformed && rx.exec(valueTransformed);
  if (!execRes) {
    return null;
  }
  let site = templateStr || '${1}'; // backward compatibility with original site separation logic
  for (const ref of references) {
    // execRes.groups, if used, is defined, as we tested for regex and siteName compatibility
    // ref.str is guarnetted to be compatible with `number` type if .isNumber is `true`
    site = site.replaceAll(
      `\${${ref.str}}`,

      // wrapping into a template literal preserves previous functionality (undefined -> 'undefined')
      // after introducing noUncheckedIndexedArrayAccess. Cleanup and better type handling
      // is suggested when this is touched in the future.
      `${ref.isNumber ? execRes[ref.str as unknown as number] : execRes.groups![ref.str]}`,
    );
  }
  return site;
}

interface DeviceForCloudSiteCalculationBase {
  vDeviceId: string;
  hostname: string;
  snmpLocation?: string | null;
  cloudResourceId: string | null;
  sn: string;
}

export interface DeviceForCloudSiteCalculation
  extends DeviceForCloudSiteCalculationBase {
  vVirtualMachinesId?: string;
  vCloudNetworkId?: string;
}

export interface MatchedDeviceInSiteCalculation
  extends DeviceForCloudSiteCalculationBase {
  siteName: string;
}

export interface VirtualMachineForSiteSeparation {
  _id: string;
  vDeviceId: string;
}

export interface CalculateCloudDevicesResult {
  devicesToAdd: MatchedDeviceInSiteCalculation[];
  rejectedCandidates: {
    forMissingVm: DeviceForCloudSiteCalculation[];
    forMissingVpc: DeviceForCloudSiteCalculation[];
  };
}

export function calculateCloudDevices(
  unmatchedDevices: DeviceForCloudSiteCalculation[],
  matchedDevices: ({ vDeviceId: string } & Record<string, any>)[],
  virtualMachines: VirtualMachineForSiteSeparation[],
): CalculateCloudDevicesResult {
  // NIM-14736 In order to move a deviced from unmatched to matched, it needs to meet following criteria:
  // 1. it must have a link to a virtual machine (vVirtualMachinesId)
  const candidates = unmatchedDevices.filter(
    (device) => device.vVirtualMachinesId,
  );
  const result: CalculateCloudDevicesResult = {
    devicesToAdd: [],
    rejectedCandidates: {
      forMissingVm: [],
      forMissingVpc: [],
    },
  };
  for (const candidate of candidates) {
    // 2. the virtual machine ID must match
    const matchingVm = virtualMachines.find(
      (vm) => candidate.vVirtualMachinesId === vm._id,
    );
    if (!matchingVm) {
      result.rejectedCandidates.forMissingVm.push(candidate);
      continue;
    }
    // 3. the virtual machine must be pointing back to a matched VPC device
    const matchingVpc = matchedDevices.find(
      (device) => device.vDeviceId === matchingVm.vDeviceId,
    );
    if (matchingVpc) {
      result.devicesToAdd.push({
        ...candidate,
        siteName: matchingVpc.siteName,
      });
    } else {
      result.rejectedCandidates.forMissingVpc.push(candidate);
    }
  }
  return result;
}

export interface MatchedCloudNetworkInSiteCalculation {
  vCloudNetworkId: string;
  siteName: string;
}

export interface CalculateCloudNetworkDevicesResult {
  devicesToAdd: MatchedDeviceInSiteCalculation[];
  rejectedCandidates: {
    forMissingVpc: DeviceForCloudSiteCalculation[];
  };
}

// NIM-24429: native-cloud-model analogue of calculateCloudDevices. No
// vVirtualMachines indirection is needed here — vCloudNetworkId already
// identifies the parent VPC/VNet directly (via vIntL3.vCloudNetworkInterfaceId).
export function calculateCloudNetworkDevices(
  unmatchedDevices: DeviceForCloudSiteCalculation[],
  matchedCloudNetworks: MatchedCloudNetworkInSiteCalculation[],
): CalculateCloudNetworkDevicesResult {
  const candidates = unmatchedDevices.filter(
    (device) => device.vCloudNetworkId,
  );
  const result: CalculateCloudNetworkDevicesResult = {
    devicesToAdd: [],
    rejectedCandidates: {
      forMissingVpc: [],
    },
  };
  const siteByCloudNetworkId = new Map(
    matchedCloudNetworks.map((net) => [net.vCloudNetworkId, net.siteName]),
  );

  for (const candidate of candidates) {
    const matchingSiteName = siteByCloudNetworkId.get(
      candidate.vCloudNetworkId as string,
    );

    if (matchingSiteName) {
      result.devicesToAdd.push({
        ...candidate,

        siteName: matchingSiteName,
      });
    } else {
      result.rejectedCandidates.forMissingVpc.push(candidate);
    }
  }
  return result;
}
